<template>
  <LocalLanding v-if="!hasResolveErrors" />
  <HgPageNotFound v-else :theme="platform" class="layout-container" />
</template>

<script setup lang="ts">
import { HgPageNotFound, HgSupportedLanguage } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { defineAsyncComponent, onServerPrefetch, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import useConditionalSSRContext from '@/composables/useConditionalSSRContext';
import {
  getLocationInfos,
  replaceCityWithMunicipality,
} from '@/helpers/GeoLocation/GeoLocationHelper';
import { getCurrentPlatform, isPlatformIs24 } from '@/helpers/Platform/Platform';
import { getLocalLandingPagePath } from '@/helpers/Url/Url';
import { useLocalLandingStore } from '@/stores/localLanding/localLanding';
import { getDesiredRoute } from './redirectionMapping';

// Composables
const localLandingStore = useLocalLandingStore();
const { locale } = useI18n();
const route = useRoute();
const router = useRouter();
const { hasResolveErrors, routeGeoLocation } = storeToRefs(localLandingStore);

// Setup context for SSR
const ctx = useConditionalSSRContext();
const context = ctx;

// Reactive data
const platform = ref(getCurrentPlatform());

// Dynamic component based on platform
const LocalLanding = defineAsyncComponent(
  () => import(isPlatformIs24() ? './LocalLandingPageIs24.vue' : './LocalLandingPageHg.vue'),
);

// Watchers
watch(locale, (newLocale: string) => {
  const locationInfos = getLocationInfos(routeGeoLocation.value, newLocale as HgSupportedLanguage);
  const path = getLocalLandingPagePath(newLocale as HgSupportedLanguage, locationInfos.urlName);
  router.push(path);
});

// Server prefetch functionality
onServerPrefetch(async () => {
  const location = route.params.location;
  if (location) {
    const language = locale.value as HgSupportedLanguage;
    const urlName = replaceCityWithMunicipality(location as string, language);

    const desiredRoute = await getDesiredRoute(urlName, language);
    if (desiredRoute && context) {
      context.redirect =
        context.url && context.url.endsWith('/') ? `../${desiredRoute}` : desiredRoute;
      return;
    }

    await localLandingStore.resolveLocationByUrl({ urlName, language });
  }
});
</script>
